import { useState, useEffect, createContext, useContext } from "react";
import Cookies from "js-cookie";
import { authAPI } from "../lib/api";

// Create Auth Context
const AuthContext = createContext({});

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Only for initial auth check
  const [error, setError] = useState(null);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = Cookies.get("token") || localStorage.getItem("accessToken");
      const savedUser = localStorage.getItem("user");

      if (token && savedUser) {
        // Validate token by fetching current user
        const response = await authAPI.getCurrentUser();
        const userData = response.data.data.user;

        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } else {
        // No token or user data, clear everything
        clearAuth();
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      // Don't set global loading for login - let components handle their own loading
      setError(null);

      console.log("useAuth: Attempting login with credentials:", credentials);
      const response = await authAPI.login(credentials);
      console.log("useAuth: Login response:", response);
      const { user: userData, accessToken } = response.data.data;

      // Store user data and token
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("accessToken", accessToken);

      // Also store in cookies for consistency
      Cookies.set("token", accessToken, { expires: 7 }); // 7 days

      return { success: true, user: userData };
    } catch (error) {
      console.log("useAuth: Login error caught:", error);
      console.log("useAuth: Error response:", error.response);
      console.log("useAuth: Error response data:", error.response?.data);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Erreur de connexion";
      console.log("useAuth: Error message to return:", errorMessage);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearAuth();
    }
  };

  const clearAuth = () => {
    setUser(null);
    setError(null);
    Cookies.remove("token");
    Cookies.remove("refreshToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const changePassword = async (passwordData) => {
    try {
      // Don't set global loading for password change - let components handle their own loading
      setError(null);

      await authAPI.changePassword(passwordData);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Erreur lors du changement de mot de passe";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const forgotPassword = async (email) => {
    try {
      // Don't set global loading - let components handle their own loading
      setError(null);

      await authAPI.forgotPassword(email);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Erreur lors de la demande de réinitialisation";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (resetData) => {
    try {
      // Don't set global loading - let components handle their own loading
      setError(null);

      await authAPI.resetPassword(resetData);
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Erreur lors de la réinitialisation du mot de passe";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Check if user has specific role
  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === "string") {
      return user.role === roles;
    }
    return roles.includes(user.role);
  };

  // Check if user can access specific feature
  const canAccess = (feature) => {
    if (!user) return false;

    // Admin and RH can access everything
    if (["admin", "rh"].includes(user.role)) {
      return true;
    }

    // Define role-based permissions
    const permissions = {
      manager: [
        "employees.read",
        "employees.update_limited",
        "attendance.read",
        "leave.approve",
        "reports.department",
      ],
      employe: [
        "profile.read",
        "profile.update_limited",
        "leave.create",
        "attendance.own",
        "payroll.own",
      ],
      stagiaire: ["profile.read", "profile.update_limited", "attendance.own"],
    };

    return permissions[user.role]?.includes(feature) || false;
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    changePassword,
    forgotPassword,
    resetPassword,
    hasRole,
    canAccess,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
